import LeaderboardEmptyState from "@/components/dashboard/leaderboards/LeaderboardEmptyState";
import LeaderboardSkeleton from "@/components/dashboard/leaderboards/LeaderboardSkeleton";
import LeaderboardTable from "@/components/dashboard/leaderboards/LeaderboardTable";
import { SeasonSelectDropdown } from "@/components/dashboard/leaderboards/SeasonSelectDropdown";
import {
    ALL_CLANS,
    ALL_SEASONS,
    TOP_PLAYERS_BY_CLAN,
} from "@/graphql/queries/leaderboardQueries";
import { Clan, LeaderboardPlayer, Season } from "@/types/leaderboards";
import { convertLetters } from "@/utils/letterConversion";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

const PAGE_SIZE = 50;

interface TopPlayersByClanGrouped {
    clanTag: string;
    players: LeaderboardPlayer[];
}

const ClanPage: React.FC = () => {
    const [selectedSeasonId, setSelectedSeasonId] = useState<
        string | undefined
    >();
    const [selectedClanTag, setSelectedClanTag] = useState<
        string | undefined
    >();
    const [clanOffsets, setClanOffsets] = useState<Record<string, number>>({});
    const [clanHasMore, setClanHasMore] = useState<Record<string, boolean>>({});

    const { data: clansData, loading: clansLoading } = useQuery<{
        allClans: Clan[];
    }>(ALL_CLANS);
    const { data: seasonsData } = useQuery<{ allSeasons: Season[] }>(
        ALL_SEASONS,
    );

    const allClans = clansData?.allClans ?? [];
    const allSeasons = seasonsData?.allSeasons ?? [];

    useEffect(() => {
        if (!selectedClanTag && allClans.length > 0) {
            setSelectedClanTag(allClans[0].tag);
        }
    }, [allClans, selectedClanTag]);

    useEffect(() => {
        if (!selectedSeasonId && allSeasons.length > 0) {
            const latest = allSeasons.find((s) => s.isActive) ?? allSeasons[0];
            if (latest) setSelectedSeasonId(latest.id);
        }
    }, [allSeasons]);

    const {
        data: topPlayersData,
        loading: topPlayersLoading,
        error: topPlayersError,
        fetchMore,
    } = useQuery<{ topPlayersByClanGrouped: TopPlayersByClanGrouped[] }>(
        TOP_PLAYERS_BY_CLAN,
        {
            variables: {
                seasonId: selectedSeasonId,
                clanOffsets,
                limit: PAGE_SIZE,
            },
            skip: !selectedSeasonId,
        },
    );

    useEffect(() => {
        if (!topPlayersData) return;

        const updated: Record<string, boolean> = {};
        topPlayersData.topPlayersByClanGrouped.forEach((group) => {
            updated[group.clanTag] = group.players.length === PAGE_SIZE;
        });

        setClanHasMore((prev) => ({ ...prev, ...updated }));
    }, [topPlayersData]);

    useEffect(() => {
        setClanOffsets({});
        setClanHasMore({});
    }, [selectedSeasonId]);

    const handleLoadMore = (tag: string) => {
        const newOffset = (clanOffsets[tag] ?? 0) + PAGE_SIZE;
        const updatedOffsets = {
            ...clanOffsets,
            [tag]: newOffset,
        };

        setClanOffsets(updatedOffsets);

        void fetchMore({
            variables: {
                seasonId: selectedSeasonId,
                clanOffsets: updatedOffsets,
                limit: PAGE_SIZE,
            },
        });
    };

    if ((clansLoading || topPlayersLoading) && !topPlayersData) {
        return <LeaderboardSkeleton />;
    }

    if (topPlayersError) {
        return (
            <LeaderboardEmptyState message="Failed to load clan leaderboard data." />
        );
    }

    const playersByClan: Record<string, LeaderboardPlayer[]> = {};
    (topPlayersData?.topPlayersByClanGrouped ?? []).forEach((group) => {
        playersByClan[group.clanTag] = group.players;
    });

    const selectedClan =
        allClans.find((clan) => clan.tag === selectedClanTag) ?? allClans[0];

    if (!selectedClan) {
        return <LeaderboardEmptyState message="No clans available." />;
    }

    const selectedClanPlayers = playersByClan[selectedClan.tag] ?? [];

    return (
        <div className="space-y-6">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                <SeasonSelectDropdown
                    seasons={allSeasons}
                    selectedSeasonId={selectedSeasonId ?? ""}
                    onSeasonChange={(id) => setSelectedSeasonId(id)}
                />
            </div>

            <div className="border-brand rounded-xl border bg-zinc-900/55 p-4">
                <div className="mb-4 flex flex-wrap gap-2">
                    {allClans.map((clan) => {
                        const isActive = clan.tag === selectedClan.tag;

                        return (
                            <button
                                key={clan.id}
                                type="button"
                                onClick={() => setSelectedClanTag(clan.tag)}
                                className={`font-prviking rounded-lg border px-3 py-2 text-sm transition sm:text-base ${
                                    isActive
                                        ? "bg-brand text-brand_charcoal border-brand"
                                        : "border-zinc-600 bg-zinc-800/70 text-zinc-200 hover:border-zinc-400"
                                }`}
                            >
                                {convertLetters(clan.tag)}
                            </button>
                        );
                    })}
                </div>

                <h3 className="font-prviking text-brand mb-3 text-center text-2xl">
                    {convertLetters(selectedClan.tag)}
                </h3>

                {selectedClanPlayers.length > 0 ? (
                    <>
                        <LeaderboardTable players={selectedClanPlayers} />
                        {clanHasMore[selectedClan.tag] && (
                            <div className="mt-3 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleLoadMore(selectedClan.tag)
                                    }
                                    className="font-prviking bg-brand text-brand_charcoal border-brand hover:bg-brand/80 rounded-lg border px-3 py-1 transition"
                                >
                                    {convertLetters("Load More")}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <LeaderboardEmptyState
                        message={`No data for ${selectedClan.tag}`}
                    />
                )}
            </div>
        </div>
    );
};

export default ClanPage;
